import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import translations from './translations';

const LanguageContext = createContext();

const STORAGE_KEY = 'lang';
const SUPPORTED = ['tr', 'en'];

/**
 * The language has to be decided **before the first paint**, not after a
 * network round trip.
 *
 * This used to start on `'en'` unconditionally and then ask geojs.io for the
 * visitor's country, so every load rendered the whole app in English and
 * re-rendered it in Turkish a second or two later. On the login and sign-up
 * forms that lands mid-typing: every label and placeholder changes under the
 * hand filling them in, which reads as the page having reloaded. It also ran
 * on *every* load, and the answer overwrote whatever `setLang` had been told,
 * so a language the user picked could not survive.
 *
 * Order now: a stored decision, then the browser's own language, and only
 * then the network — and the network is asked only when nothing else has
 * answered. Each step is synchronous except the last, so the common case
 * paints in the right language immediately.
 */

const readStored = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return SUPPORTED.includes(stored) ? stored : null;
    } catch {
        // Private mode / a locked-down WebView. Not being able to remember the
        // choice is survivable; throwing on the way to the first render is not.
        return null;
    }
};

const store = (lang) => {
    try {
        localStorage.setItem(STORAGE_KEY, lang);
    } catch {
        /* see readStored */
    }
};

const fromBrowser = () => (
    navigator.language && navigator.language.toLowerCase().startsWith('tr') ? 'tr' : 'en'
);

export function LanguageProvider({ children }) {
    // Lazy initialiser: this runs once, before the first render, so there is
    // nothing to flip afterwards for anyone whose browser already says which
    // language they read.
    const [lang, setLang] = useState(() => readStored() || fromBrowser());

    useEffect(() => {
        // A stored decision is final — no request, and nothing to override it.
        if (readStored()) return undefined;

        let cancelled = false;
        const detect = async () => {
            try {
                const res = await fetch('https://get.geojs.io/v1/ip/country.json');
                const data = await res.json();
                if (cancelled) return;
                const detected = data.country === 'TR' ? 'tr' : 'en';
                // Remembered so this is the last load that can flip: the same
                // visitor gets the right language from the first paint next
                // time, with no request at all.
                store(detected);
                setLang(detected);
            } catch {
                // The browser's guess is already on screen; keep it.
            }
        };
        detect();
        return () => { cancelled = true; };
    }, []);

    // Exposed under the old name because it is the same job — it just sticks
    // now. Anything that offers a language switcher calls this.
    const choose = useCallback((next) => {
        if (!SUPPORTED.includes(next)) return;
        store(next);
        setLang(next);
    }, []);

    const t = useCallback((key) => {
        const keys = key.split('.');
        let val = translations[lang];
        for (const k of keys) {
            if (val === undefined) return key;
            val = val[k];
        }
        return val !== undefined ? val : key;
    }, [lang]);

    // Stable identity: every screen in the app consumes this, and a fresh
    // object each render re-renders all of them for nothing.
    const value = useMemo(() => ({ lang, setLang: choose, t }), [lang, choose, t]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useTranslation() {
    return useContext(LanguageContext);
}
